<template>
    <div class="d-flex flex-column overflow-hidden h-100">
        <vue-custom-scrollbar class="app-content-padding flex-grow-1 overflow-hidden h-100">
            <ts-page-title 
                :title="$t('dailyCloseout.pageTitle')" 
                :breadcrumb="[
                    { text: $t('home'), href: '/' }, 
                    { text: $t('dailyCloseout.pageTitle'), href: '/admin/daily-closeouts' }, 
                    { text: $t('create'), active: true }
                ]" 
            />

            <!-- Filter Toolbar -->
            <FilterToolbar
                :selected-date.sync="selectedDate"
                :branches="branches"
                :branch.sync="filters.branch"
                :co-name.sync="filters.coName"
                :loading="loading"
                :has-cash-to-clear="hasCashToClear"
                @date-change="onDateChange"
                @branch-change="applyFilters"
                @co-name-change="applyFilters"
                @refresh="fetchResource"
                @clear-all-cash="clearAllCash"
                @go-back="$router.push('/admin/daily-closeouts')"
            />

            <!-- Summary Cards -->
            <SummaryCards
                :total-c-os="totalCOs"
                :summary="totalSummary"
            />

            <!-- Data Table -->
            <CODataTable
                :data="filteredResources"
                :data-count="filteredResources.length - 1"
                :loading="loading"
                :repayment-details="repaymentDetails"
                :loading-repayments="loadingRepayments"
                @clear-co="clearCashByCO"
                @fetch-repayments="fetchCoRepayments"
                @clear-repayments="clearCoRepayments"
            />
        </vue-custom-scrollbar>
        
        <!-- Receipt Modal -->
        <ReceiptModal 
            v-if="receiptData" 
            :visible.sync="showReceipt" 
            :receipt="receiptData" 
            @close="closeReceipt" 
        />
    </div>
</template>

<script>
import AppOptions from "@/config/AppOptions.vue";
import { Errors } from "form-backend-validation";
import moment from "moment";
import ReceiptModal from "./ReceiptModal-Simple.vue";
import FilterToolbar from "./FilterToolbar.vue";
import SummaryCards from "./SummaryCards.vue";
import CODataTable from "./CODataTable.vue";

export default {
    name: "createDailyCloseout",
    components: {
        ReceiptModal,
        FilterToolbar,
        SummaryCards,
        CODataTable
    },
    data() {
        return { 
            loading: false, 
            errors: new Errors(), 
            resources: [], 
            transactionDate: moment().format('YYYY-MM-DD'), 
            selectedDate: moment().format('DD-MM-YYYY'), 
            filters: { 
                branch: null, 
                coName: "" 
            },
            showReceipt: false,
            receiptData: null,
            repaymentDetails: {},
            loadingRepayments: {}
        };
    },
    created() {
        AppOptions.appContentFullHeight = true;
        AppOptions.appContentClass = "p-0";
    },
    computed: {
        branches() { 
            return [...new Set(this.resources.filter(r => r.co_code !== "TOTAL").map(r => r.branch_code))].sort(); 
        },
        filteredResources() {
            let filtered = this.resources.filter(r => r.co_code !== "TOTAL");
            if (this.filters.branch) filtered = filtered.filter(r => r.branch_code === this.filters.branch);
            if (this.filters.coName) filtered = filtered.filter(r => r.co_name.toLowerCase().includes(this.filters.coName.toLowerCase()));
            return [...filtered, this.calculateTotalRow(filtered)];
        },
        totalCOs() { 
            return this.resources.filter(r => r.co_code !== 'TOTAL').length; 
        },
        totalSummary() { 
            return this.calculateTotalRow(this.resources.filter(r => r.co_code !== 'TOTAL')); 
        },
        hasCashToClear() { 
            return this.filteredResources.some(r => r.co_code !== 'TOTAL' && ((r.cash_khr && r.cash_khr > 0) || (r.cash_usd && r.cash_usd > 0))); 
        }
    },
    methods: {
        onDateChange(date) {
            if (date) {
                this.transactionDate = moment(date, 'DD-MM-YYYY').format('YYYY-MM-DD');
                this.fetchResource();
            }
        },
        fetchResource() {
            this.loading = true;
            this.$store.dispatch("creditOperation/dailyCloseout/fetchCreateResources", { 
                transaction_date: this.transactionDate 
            })
                .then(response => { 
                    if (response.success) this.processData(response.data); 
                    else this.$Message.error(response.message || 'Failed to load data'); 
                })
                .catch(error => this.$Message.error(error.message || 'An error occurred'))
                .finally(() => { this.loading = false; });
        },
        processData(data) {
            this.transactionDate = data.transaction_date;
            this.resources = data.collection_officers.map(co => ({
                ...co,
                branch_code: co.branch_code,
                cash_khr: co.payment_methods.cash.khr,
                cash_usd: co.payment_methods.cash.usd,
                aba_khr: co.payment_methods.bank_aba.khr,
                aba_usd: co.payment_methods.bank_aba.usd,
                acleda_khr: co.payment_methods.bank_acleda.khr,
                acleda_usd: co.payment_methods.bank_acleda.usd
            }));
        },
        calculateTotalRow(dataRows) {
            const totals = dataRows.reduce((acc, row) => {
                acc.requested_amount += row.requested_amount || 0;
                acc.disbursed_amount += row.disbursed_amount || 0;
                acc.returned_amount += row.returned_amount || 0;
                acc.actual_disbursed += row.actual_disbursed || 0;
                acc.disbursed_collected += row.disbursed_collected || 0;
                acc.actual_collected += row.actual_collected || 0;
                acc.cash_khr += row.cash_khr || 0;
                acc.cash_usd += row.cash_usd || 0;
                acc.aba_khr += row.aba_khr || 0;
                acc.aba_usd += row.aba_usd || 0;
                acc.acleda_khr += row.acleda_khr || 0;
                acc.acleda_usd += row.acleda_usd || 0;
                return acc;
            }, { 
                requested_amount: 0, 
                disbursed_amount: 0, 
                returned_amount: 0, 
                actual_disbursed: 0, 
                disbursed_collected: 0, 
                actual_collected: 0, 
                cash_khr: 0, 
                cash_usd: 0, 
                aba_khr: 0, 
                aba_usd: 0, 
                acleda_khr: 0, 
                acleda_usd: 0 
            });
            totals.bank_khr = (totals.aba_khr || 0) + (totals.acleda_khr || 0);
            totals.bank_usd = (totals.aba_usd || 0) + (totals.acleda_usd || 0);
            return { 
                co_code: "TOTAL", 
                count_info: `${dataRows.length} record(s)`, 
                ...totals, 
                _disabled: true 
            };
        },
        applyFilters() {},
        clearCashByCO(row) {
            this.$Modal.confirm({
                title: "Clear by CO (Credit Officer)",
                content: `<div class="tw-py-2"><p class="tw-mb-3">Are you sure you want to clear money for this Credit Officer?</p><p class="tw-font-semibold tw-text-lg tw-mb-3">${row.co_name}</p></div>`,
                onOk: () => {
                    this.loading = true;
                    const payload = { 
                        co_id: row.co_id, 
                        branch_id: row.branch_id, 
                        transaction_date: this.transactionDate, 
                        requested_amount: row.requested_amount || 0, 
                        disbursed_amount: row.disbursed_amount || 0, 
                        returned_amount: row.returned_amount || 0, 
                        actual_disbursed: row.actual_disbursed || 0, 
                        disbursed_collected: row.disbursed_collected || 0, 
                        actual_collected: row.actual_collected || 0, 
                        payment_methods: { 
                            cash: { 
                                khr: row.cash_khr || 0, 
                                usd: row.cash_usd || 0 
                            }, 
                            bank_aba: { 
                                khr: row.aba_khr || 0, 
                                usd: row.aba_usd || 0 
                            }, 
                            bank_acleda: { 
                                khr: row.acleda_khr || 0, 
                                usd: row.acleda_usd || 0 
                            } 
                        } 
                    };
                    this.$store.dispatch("creditOperation/dailyCloseout/clearCash", payload)
                        .then(response => { 
                            if (response.success) { 
                                this.$Message.success({ content: "Cash cleared successfully", duration: 3 });
                                
                                // Show receipt if available
                                if (response.data && response.data.receipt) {
                                    this.receiptData = response.data.receipt;
                                    this.showReceipt = true;
                                }
                                
                                this.fetchResource(); 
                            } else {
                                this.$Message.error(response.message || "Failed to clear cash"); 
                            }
                        })
                        .catch(error => this.$Message.error(error.message || "An error occurred while clearing cash"))
                        .finally(() => { this.loading = false; });
                }
            });
        },
        closeReceipt() {
            this.showReceipt = false;
            this.$nextTick(() => {
                this.receiptData = null;
            });
        },
        clearAllCash() {
            const cosWithCash = this.filteredResources.filter(r => r.co_code !== 'TOTAL' && ((r.cash_khr && r.cash_khr > 0) || (r.cash_usd && r.cash_usd > 0)));
            if (cosWithCash.length === 0) { 
                this.$Message.warning('No cash to clear'); 
                return; 
            }
            this.$Modal.confirm({
                title: "Confirm Clear All Cash",
                content: `<div class="tw-py-2"><p class="tw-mb-3">Are you sure you want to clear cash for <strong class="tw-text-orange-600">${cosWithCash.length} CO(s)</strong>?</p><div class="tw-bg-yellow-50 tw-p-3 tw-rounded tw-border tw-border-yellow-200"><p class="tw-text-sm tw-text-gray-700">This action will create closeout records and clear cash for all filtered COs with cash amounts.</p></div></div>`,
                okText: "Yes, Clear All",
                cancelText: "Cancel",
                onOk: () => {
                    this.loading = true;
                    let completed = 0, failed = 0;
                    const clearPromises = cosWithCash.map(row => {
                        const payload = { 
                            co_id: row.co_id, 
                            branch_id: row.branch_id, 
                            transaction_date: this.transactionDate, 
                            requested_amount: row.requested_amount || 0, 
                            disbursed_amount: row.disbursed_amount || 0, 
                            returned_amount: row.returned_amount || 0, 
                            actual_disbursed: row.actual_disbursed || 0, 
                            disbursed_collected: row.disbursed_collected || 0, 
                            actual_collected: row.actual_collected || 0, 
                            payment_methods: { 
                                cash: { 
                                    khr: row.cash_khr || 0, 
                                    usd: row.cash_usd || 0 
                                }, 
                                bank_aba: { 
                                    khr: row.aba_khr || 0, 
                                    usd: row.aba_usd || 0 
                                }, 
                                bank_acleda: { 
                                    khr: row.acleda_khr || 0, 
                                    usd: row.acleda_usd || 0 
                                } 
                            } 
                        };
                        return this.$store.dispatch("creditOperation/dailyCloseout/clearCash", payload)
                            .then(response => { 
                                if (response.success) completed++; 
                                else failed++; 
                            })
                            .catch(() => { failed++; });
                    });
                    Promise.all(clearPromises).then(() => {
                        if (failed === 0) this.$Message.success({ content: `All cash cleared successfully (${completed} CO(s))`, duration: 5 });
                        else this.$Message.warning({ content: `Cash cleared for ${completed} CO(s), ${failed} failed`, duration: 5 });
                        this.fetchResource();
                    }).finally(() => { this.loading = false; });
                }
            });
        },
        
        async fetchCoRepayments(row) {
            const coId = row.co_id;
            
            if (this.loadingRepayments[coId] || this.repaymentDetails[coId]) {
                return;
            }
            
            this.$set(this.loadingRepayments, coId, true);
            
            try {
                const response = await this.$store.dispatch("creditOperation/dailyCloseout/fetchCoRepayments", {
                    co_id: row.co_id,
                    branch_id: row.branch_id,
                    transaction_date: this.transactionDate
                });
                
                if (response.success) {
                    this.$set(this.repaymentDetails, coId, response.data);
                } else {
                    this.$Message.error('Failed to load repayment details');
                }
            } catch (error) {
                console.error('Error loading repayment details:', error);
                this.$Message.error('Error loading repayment details');
            } finally {
                this.$set(this.loadingRepayments, coId, false);
            }
        },
        
        clearCoRepayments(coId) {
            // Clear repayment data when row is collapsed
            this.$delete(this.repaymentDetails, coId);
            this.$delete(this.loadingRepayments, coId);
        }
    },
    beforeRouteEnter(to, from, next) { 
        next(vm => { vm.fetchResource(); }); 
    },
    beforeRouteLeave(to, from, next) { 
        AppOptions.appContentFullHeight = false; 
        AppOptions.appContentClass = ""; 
        next(); 
    }
};
</script>

<style scoped>
::v-deep .ivu-input-number-input { 
    text-align: right; 
}
</style>
